// Imports for external libraries go here.
import React, { FC, useRef } from 'react';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import { Badge, Image, Button, useWindowSize, Heading, headingType, size } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';

import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { IMetaData, ITrackingProperties, MerchandisingSerpBannerProps } from './MerchandisingSerpBanner.types';
import { StyledMerchandisingSerpBanner } from './MerchandisingSerpBanner.styles';
import { getTrackingPropertiesDynamic } from '../../utils/helpers';

const removeParentTagRTE = (textRTE: string): string => {
  return textRTE.substring(textRTE.indexOf('>') + 1, textRTE.lastIndexOf('<')) || textRTE;
};

// Use named rather than default exports.
export const MerchandisingSerpBannerComponent: FC<MerchandisingSerpBannerProps> = props => {
  const { datalayer } = useSession();
  const {
    mboxParameter,
    metaData,
    logo,
    image,
    bullet1,
    bullet2,
    bullet3,
    eyebrowText,
    headerMedium,
    descriptionLong,
    destinationUrl1,
    openInNewTab = false,
    callToActionText1,
    openInNewWindowAriaLabel,
    trackingProperties = {} as ITrackingProperties,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
    displayEvergreenContent = true,
    styleclass = '',
  } = props;
  const ref = useRef();
  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    trackingProperties,
    metaData || ({} as IMetaData),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props as any,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    mboxParameter
  );
  const { width = 0 } = useWindowSize();
  const sanitizedNode = DOMPurify.sanitize(descriptionLong, { RETURN_DOM: true });
  const fragmentString = (sanitizedNode as unknown as HTMLElement)?.innerHTML;
  return (
    <StyledMerchandisingSerpBanner
      data-testid="merch-MerchandisingSerpBanner"
      data-component-name="o-merch-MerchandisingSerpBanner"
      className={clsx(`container-fluid px-3 px-lg-0 my-4 ${styleclass}`, {
        'd-none': isTargetContentError && String(displayEvergreenContent) === 'false',
      })}
    >
      <div className="merchandising-serp-banner container p-0 d-block d-md-flex justify-content-between t-background-color">
        <div
          className={clsx('merchandising-serp-banner__image_container', { 'skeleton-loader': !isTargetContentLoaded })}
        >
          {isTargetContentLoaded && (
            <>
              <Image
                customClass={clsx('merchandising-serp-banner__image')}
                altText={image?.altText ?? ''}
                renditions={image?.renditions}
                dynamic={image?.dynamic}
                defaultImageURL={image?.assetPath}
              />
              <Image
                customClass={clsx('merchandising-serp-banner__image-logo')}
                altText={logo?.altText ?? ''}
                dynamic={false}
                title={logo?.title}
                defaultImageURL={logo?.imageSrc}
                loading={'lazy'}
              />
            </>
          )}
        </div>

        <div className="merchandising-serp-banner__content p-3 pl-lg-4 pt-lg-4 d-flex flex-column">
          <Badge
            badgeText={isTargetContentLoaded ? eyebrowText : ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            badgeVariation={'inline' as any}
            customClass={clsx('merchandising-serp-banner__content-badge m-badge-overlay-merch p-2 mb-2 mb-md-3', {
              'skeleton-loader pb-4': !isTargetContentLoaded,
              'd-none': isTargetContentLoaded && !eyebrowText,
            })}
          />
          <Heading
            data-testid="heading"
            customClass={clsx('merchandising-serp-banner__content-heading', 'mb-2 mb-md-3', {
              'skeleton-loader pb-4': !isTargetContentLoaded,
            })}
            variation={headingType.subtitle}
            fontSize={width < 576 ? size.medium : width > 992 ? size.extraLarge : size.large}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            element={'h2' as any}
            // Using removeParentTagRTE to clean the text recieved from RTE
            dangerouslySet={isTargetContentLoaded && headerMedium?.length > 0 ? removeParentTagRTE(headerMedium) : ''}
          />
          {isTargetContentLoaded ? (
            <div
              data-testid="description"
              className={clsx(
                'merchandising-serp-banner__content-description',
                'd-none d-lg-block',
                't-font-alt-xs',
                'mb-2 mb-md-3'
              )}
              dangerouslySetInnerHTML={{ __html: fragmentString }}
            />
          ) : (
            <div className="d-none d-lg-block skeleton-loader pb-5 mb-2 mb-md-3"></div>
          )}
          <div className={clsx('merchandising-serp-banner__content-bullets')}>
            {[bullet1, bullet2, bullet3].map((text, index) => (
              <React.Fragment key={text}>
                {isTargetContentLoaded && text && (
                  <p data-testid={`bullet-${index}`} key={text} className="d-flex align-items-center mb-1">
                    <span className="icon-success icon-decorative" />
                    <span className="ml-2 t-font-xs">{text}</span>
                  </p>
                )}
                {!isTargetContentLoaded && <p key={index} className="skeleton-loader py-2 mb-1" />}
              </React.Fragment>
            ))}
          </div>

          <div className={clsx('d-flex justify-content-end mt-auto')}>
            {isTargetContentLoaded && callToActionText1 ? (
              <Button
                className={clsx(
                  'merchandising-serp-banner__content-button',
                  `m-button-s text-center mt-2 pt-0 align-content-center`
                )}
                href={destinationUrl1}
                isLink={true}
                target={openInNewTab === true ? '_blank' : '_self'}
                trackingProperties={trackingPropertiesDynamic}
                rel={`${openInNewTab ? 'noopener noreferrer' : ''}`}
                ref={ref}
                buttonCopy={callToActionText1}
                linkAriaLabelOpenNewWindow={openInNewWindowAriaLabel as unknown as string}
                compName={trackingProperties?.trackingContentPosition}
                data-is-click-track={trackingProperties?.isClickTrack}
              />
            ) : (
              <span className="skeleton-loader px-5 py-3 mt-2" />
            )}
          </div>
        </div>
      </div>
    </StyledMerchandisingSerpBanner>
  );
};

const MerchandisingSerpBannerConfig = {
  emptyLabel: 'MerchandisingSerpBanner',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/serpbanner`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingSerpBanner = (props: any) => {
  return (
    <EditableComponent
      config={MerchandisingSerpBannerConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MerchandisingSerpBannerComponent {...({} as any)} />}
    />
  );
};
